/* eslint-disable @typescript-eslint/no-use-before-define, no-underscore-dangle, no-else-return */
import { PostHogProvider as BasePostHogProvider } from '@posthog/react';
import {
  type CaptureOptions,
  type CaptureResult,
  type PostHog,
  type Properties,
  posthog,
} from 'posthog-js';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import {
  REACT_APP_POSTHOG_API_KEY,
  REACT_APP_POSTHOG_DEBUG,
  REACT_APP_POSTHOG_HOST,
  REACT_APP_POSTHOG_UI_HOST,
} from '../runtimeEnvVars';

import type { AnalyticsEvent } from '../utils/analytics';
import { sanitizePostHogEvent } from '../utils/posthogSanitize';

if (REACT_APP_POSTHOG_API_KEY) {
  posthog.init(REACT_APP_POSTHOG_API_KEY, {
    // Coerce a missing host to `undefined` so posthog-js keeps its own default
    // host. Its config merge only skips `undefined`, so any other falsy value —
    // and the CI build pins `REACT_APP_POSTHOG_HOST: ""` — would overwrite the
    // default with an unusable host instead of falling back.
    api_host: REACT_APP_POSTHOG_HOST || undefined,
    ui_host: REACT_APP_POSTHOG_UI_HOST || 'https://us.posthog.com',
    // `defaults` is a PostHog-originated identifier for a bundle of settings
    // defined as-of a particular date, in our case the defaults as of 2025-11-30
    defaults: '2025-11-30',
    // only capture events after we call `identify`
    person_profiles: 'identified_only',
    // enables autocapturing $pageview events on SPAs by hooking into history
    // change events
    capture_pageview: 'history_change',
    capture_pageleave: false,
    autocapture: {
      // disable autocapturing DOM events
      dom_event_allowlist: [],
    },
    before_send: sanitizePostHogEvent,
    debug: REACT_APP_POSTHOG_DEBUG === 'true',
  });
}

export interface PostHogProxy extends PostHog {
  _rawClient: PostHog;
  _defaultProperties: Properties | undefined | null;

  capture<S extends string>(
    name: AnalyticsEvent<S>,
    properties?: Properties | null,
    options?: CaptureOptions,
  ): CaptureResult | undefined;
}

export interface PostHogWithDefaults {
  posthog: PostHogProxy;
}

export const PostHogContext = createContext<PostHogWithDefaults>({
  posthog: makeProxy(posthog, {}),
});

export interface PostHogProviderProps {
  properties?: Properties;
  children: ReactNode;
}

export function PostHogRootProvider({
  properties = {},
  children,
}: PostHogProviderProps) {
  const wrapped = useMemo(() => wrapPostHog(posthog, properties), [properties]);

  return (
    <PostHogContext.Provider
      value={{
        posthog: wrapped,
      }}
    >
      <BasePostHogProvider client={wrapped}>{children}</BasePostHogProvider>
    </PostHogContext.Provider>
  );
}

export function PostHogPropertiesProvider({
  properties = {},
  children,
}: PostHogProviderProps) {
  const { posthog } = useContext(PostHogContext);
  const wrapped = useMemo(
    () => wrapPostHog(posthog, properties),
    [posthog, properties],
  );

  return (
    <PostHogContext.Provider
      value={{
        posthog: wrapped,
      }}
    >
      <BasePostHogProvider client={wrapped}>{children}</BasePostHogProvider>
    </PostHogContext.Provider>
  );
}

export function wrapPostHog(
  posthog: PostHog | PostHogProxy,
  properties?: Properties,
): PostHogProxy {
  if ((posthog as PostHogProxy)._rawClient !== undefined) {
    const cast = posthog as PostHogProxy;

    return makeProxy(cast._rawClient, {
      ...(cast._defaultProperties ?? {}),
      ...(properties ?? {}),
    });
  } else {
    return makeProxy(posthog, properties ?? {});
  }
}

function makeProxy(posthog: PostHog, defaults: Properties): PostHogProxy {
  function capture<S extends string>(
    name: AnalyticsEvent<S>,
    properties?: Properties | null,
    options?: CaptureOptions,
  ): CaptureResult | undefined {
    const mergedProperties =
      properties === null ? null : { ...defaults, ...(properties ?? {}) };
    return posthog.capture(name, mergedProperties, options);
  }

  const handlers = {
    get(target: PostHog, prop: string | symbol, receiver: unknown): unknown {
      if (prop === 'capture') {
        return capture;
      } else if (prop === '_rawClient') {
        return target;
      } else if (prop === '_defaultProperties') {
        return defaults as unknown;
      }

      return Reflect.get(target, prop, receiver) as unknown;
    },
  };

  return new Proxy(posthog, handlers) as PostHogProxy;
}
